import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, Card, Grid, Text, TextField } from '@aws-amplify/ui-react';
import PageBaseNoSignIn from '@components/pages/PageBaseNoSignIn';
import { useSetRecoilState } from 'recoil';

import { getEmailFromTokenApi } from '@apis/index';
import { MessageDialogState } from '@atom/DialogState';
import { validateNumber } from '@utils/ValidationUtils';

import { GetEmailFromTokenQueryVariables } from '../../../API';
import { GraphQLErrorResponse } from '../commonType';

const GroupRegisterPage = () => {
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const navigate = useNavigate();

  // 確認画面で修正するボタン押下時に送信されるパラメータ
  const location = useLocation();
  const { fixParam } = location.state ?? '';

  // 団体名
  const [groupName, setGroupName] = useState<string>('');
  // 代表者名
  const [representativeName, setRepresentativeName] = useState<string>('');
  // 電話番号
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneNumberErrorMessage, setPhoneNumberErrorMessage] = useState<string>('');
  // 郵便番号
  const [zipCode, setZipCode] = useState<string>('');
  const [zipCodeErrorMessage, setZipCodeErrorMessage] = useState<string>('');
  // 住所
  const [adress, setAdress] = useState<string>('');
  // 名簿（ファイル名一覧）
  const [nameList, setNameList] = useState<string>('未選択');
  // 名簿（ファイル）
  const [nameListFiles, setNameListFiles] = useState<FileList | null>(null);
  // 施設ID
  const [facilityId, setFacilityId] = useState<string>('');
  // メールトークン
  const [mailToken, setMailToken] = useState<string>('');
  // メールアドレス
  const [email, setEmail] = useState<string>('');

  const [nameListErrorMessage, setNameListErrorMessage] = useState<string>('');

  // クエリストリングから取得
  const [searchParams] = useSearchParams();

  // 画面遷移時処理
  const confirm = () => {
    const confirmParam = {
      groupName,
      representativeName,
      zipCode,
      adress,
      phoneNumber,
      nameListFiles,
      facilityId,
      email
    };
    navigate('/c002/confirm', { state: { confirmParam } });
  };

  const inputRef = useRef<HTMLInputElement>(null);

  const fileUpload = () => {
    inputRef.current?.click();
  };

  // 選択したファイルをクリアする関数
  const clearFile = () => {
    setNameListFiles(null);
    setNameList('未選択');
  };

  // 許可する拡張子
  const allowExts = '.(heic|jpg|jpeg|png|pdf|xlsx|xls|doc|docs)$';

  // ファイルアップロード時の処理
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0 && files.length <= 3) {
      // ファイル名の一覧を取得して配列に保存
      const names: string[] = [];
      for (let fileCount = 0; fileCount < files.length; fileCount += 1) {
        names.push(files[fileCount].name);

        // 小文字に変換
        const ext = files[fileCount].name.toLowerCase();
        // 許可する拡張子の一覧(allow_exts)から対象の拡張子があるか確認する
        if (!ext.match(allowExts)) {
          setNameListErrorMessage(
            ' heic, jpg, jpeg, png, pdf, xlsx, xls, doc, docs 以外のファイルはアップロードできません。'
          );
          return;
        }
      }
      setNameList(names.join(', '));
      setNameListFiles(files);
      setNameListErrorMessage('');
    } else if (files && files.length > 3) {
      // 3つまでのメッセージを表示
      setNameListErrorMessage('団体名簿は3ファイル以上アップロードできません。');
    }
  };

  // メールアドレス取得
  const getemail = async () => {
    try {
      const input: GetEmailFromTokenQueryVariables = {
        input: {
          token: mailToken
        }
      };

      const result = await getEmailFromTokenApi(input);
      setEmail(result.email);

    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
  
      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in (error as GraphQLErrorResponse) && (error as GraphQLErrorResponse).errors.length !== 0) {
        message = (error as GraphQLErrorResponse).errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
      // メールアドレスの登録ページに移動させる
      navigate('/c007/register/');
    }
  };

  useEffect(() => {
    // 初回実行時の処理
    if (!fixParam) {
      // 初回画面表示遷移時の処理
      setFacilityId(searchParams.get('facilityId') ?? '');
      setMailToken(searchParams.get('mailToken') ?? '');
    }
  }, []);

  useEffect(() => {
    // fixParamが変化したときの処理
    if (fixParam) {
      setGroupName(fixParam.groupName);
      setRepresentativeName(fixParam.representativeName);
      setZipCode(fixParam.zipCode);
      setAdress(fixParam.adress);
      setPhoneNumber(fixParam.phoneNumber);
      setFacilityId(fixParam.facilityId ?? '');
      setEmail(fixParam.email ?? '');
      setNameListFiles(fixParam.nameListFiles ?? null);
      const file: FileList = fixParam.nameListFiles;
      if (file && file.length <= 3) {
        const names: string[] = [];
        for (let fileCount = 0; fileCount < file.length; fileCount += 1) {
          names.push(file[fileCount].name);
        }
        setNameList(names.join(', '));
      }
    }
  }, [fixParam]);

  useEffect(() => {
    // mailTokenが変化したときの処理
    if (mailToken) {
      getemail();
    }
  }, [mailToken]);

  return (
    <PageBaseNoSignIn title="団体登録申請">
      <Grid templateColumns={{ base: '1fr', large: '1fr' }}>
        <Card>
          <TextField
            label={
              <Text>
                団体名
                <Text as="span" fontSize="0.8rem" color="red">
                  {'  '}
                  必須
                </Text>
              </Text>
            }
            name="groupName"
            maxLength={20}
            value={groupName}
            onChange={(event) => {
              setGroupName(event.target.value);
            }}
          />
        </Card>
        <Card>
          <TextField
            label={
              <Text>
                代表者氏名
                <Text as="span" fontSize="0.8rem" color="red">
                  {'  '}
                  必須
                </Text>
              </Text>
            }
            name="representativeName"
            maxLength={20}
            value={representativeName}
            onChange={(event) => {
              setRepresentativeName(event.target.value);
            }}
          />
        </Card>
        <Card>
          <TextField
            label={
              <Text>
                郵便番号(ハイフン不要)
                <Text as="span" fontSize="0.8rem" color="red">
                  {'  '}
                  必須
                </Text>
              </Text>
            }
            name="zipCode"
            minLength={7}
            maxLength={7}
            value={zipCode}
            hasError={!!zipCodeErrorMessage}
            errorMessage={zipCodeErrorMessage}
            onChange={(event) => {
              if (!validateNumber(event.target.value)) {
                setZipCodeErrorMessage('郵便番号は半角数値7文字で入力してください。');
              } else if (event.target.value.length !== 7) {
                setZipCodeErrorMessage('郵便番号は半角数値7文字で入力してください。');
              } else {
                setZipCodeErrorMessage('');
              }
              setZipCode(event.target.value);
            }}
          />
        </Card>
        <Card>
          <TextField
            label={
              <Text>
                住所
                <Text as="span" fontSize="0.8rem" color="red">
                  {'  '}
                  必須
                </Text>
              </Text>
            }
            name="adress"
            maxLength={100}
            value={adress}
            onChange={(event) => {
              setAdress(event.target.value);
            }}
          />
        </Card>
        <Card>
          <TextField
            label={
              <Text>
                電話番号(ハイフン不要)
                <Text as="span" fontSize="0.8rem" color="red">
                  {'  '}
                  必須
                </Text>
              </Text>
            }
            name="phoneNumber"
            maxLength={11}
            value={phoneNumber}
            hasError={!!phoneNumberErrorMessage}
            errorMessage={phoneNumberErrorMessage}
            onChange={(event) => {
              if (!validateNumber(event.target.value)) {
                setPhoneNumberErrorMessage('電話番号は半角数値で入力してください。');
              } else {
                setPhoneNumberErrorMessage('');
              }
              setPhoneNumber(event.target.value);
            }}
          />
        </Card>
        <Card>
          <TextField
            label="団体名簿のアップロード(3ファイルまでアップロード可能)"
            name="nameList"
            value={nameList}
            disabled
            readOnly
            hasError={!!nameListErrorMessage}
            errorMessage={nameListErrorMessage}
            outerEndComponent={
              <>
                <Button onClick={fileUpload} backgroundColor="white" color="black" borderColor="black">
                  アップロード
                </Button>
                <Button onClick={clearFile} backgroundColor="white" color="black" borderColor="black">
                  クリア
                </Button>
                <input
                  type="file"
                  onChange={handleFileChange}
                  ref={inputRef}
                  accept=".heic,.jpg,.jpeg,.png,.pdf,.xlsx,.xls,.doc,.docs"
                  multiple
                  hidden
                />
              </>
            }
          />
        </Card>
        <Card>
          <Button
            variation="primary"
            isFullWidth
            onClick={confirm}
            disabled={
              !groupName ||
              !representativeName ||
              !zipCode ||
              !adress ||
              !phoneNumber ||
              phoneNumberErrorMessage !== '' ||
              zipCodeErrorMessage !== '' 
            }
          >
            内容を確認
          </Button>
        </Card>
      </Grid>
    </PageBaseNoSignIn>
  );
};

export default GroupRegisterPage;
